import React from "react";

import {
  DefaultButton,
  DetailsRow,
  IColumn,
  Stack,
  Text,
  TextBadge,
  TextBadgeSize,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { DangerButton } from "@components/buttons/DangerButton";
import { SectionTitle } from "@components/SectionTitle";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import { withPermissions } from "@components/withPermissions";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  SoftwarePackage,
  SoftwarePackageDefault,
  SoftwarePackageType,
  SoftwarePublisher
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  usePurgeFieldmanCDN,
  useRegenerateDefaultFieldConfig,
  useSoftwarePackageDefaults,
  useSoftwarePackages,
  useSoftwarePublishers
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SoftwarePackageDefaultCurrentVersion } from "../common/SoftwarePackageDefaultCurrentVersion";
import { getTypeBadgeColor } from "../utils";
import { SoftwarePackageDefaultHost } from "./SoftwarePackageDefaultHost";
import { SoftwarePackageDefaultInstallTypeForm } from "./SoftwarePackageDefaultInstallTypeForm";

export interface SoftwarePackageDefaultSummaryDto
  extends SoftwarePackageDefault {
  name: string;
  code: string;
  softwarePackageType: string;
  softwarePublisherCode: string;
}

const validSoftwarePackageTypes = [
  SoftwarePackageType.Service,
  SoftwarePackageType.Application,
  SoftwarePackageType.File
];

const REGENERATE_TOOLTIP =
  "Recreates the default stored field configuration files in blob storage if they are corrupted or not present. Also initiates a purge on the CDN.";

const PURGE_TOOLTIP =
  "Manually purges the CDN endpoint which is used to distribute the field configuration files. It may take up to 10 minutes to complete the purge request.";

const FieldConfigScreenBase: React.FC = () => {
  const theme = useTheme();
  const regenerateDefaultFieldConfig = useRegenerateDefaultFieldConfig();
  const purgeFieldmanCDN = usePurgeFieldmanCDN();

  const softwarePackageDefaultsQuery = useSoftwarePackageDefaults();
  const {
    data: softwarePackageDefaults,
    isLoading: softwarePackageDefaultsIsLoading,
    error: softwarePackageDefaultsError
  } = softwarePackageDefaultsQuery;

  const softwarePublishersQuery = useSoftwarePublishers();
  const {
    data: softwarePublishers,
    isLoading: softwarePublishersIsLoading,
    error: softwarePublishersError
  } = softwarePublishersQuery;

  const softwarePackagesQuery = useSoftwarePackages({});

  const {
    data: softwarePackages,
    isLoading: softwarePackagesIsLoading,
    error: softwarePackagesError
  } = softwarePackagesQuery;

  const error =
    softwarePackageDefaultsError ??
    softwarePackagesError ??
    softwarePublishersError;

  const isLoading =
    softwarePackageDefaultsIsLoading ??
    softwarePackagesIsLoading ??
    softwarePublishersIsLoading;

  const items = getSummaryData(
    softwarePackageDefaults!,
    softwarePackages!,
    softwarePublishers!
  );

  const columns: IColumn[] = [
    {
      name: "Type",
      key: "softwarePackageType",
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
      onRender: (item: SoftwarePackageDefaultSummaryDto) => (
        <Stack
          wrap
          styles={{
            root: { whiteSpace: "initial" }
          }}
        >
          <TextBadge
            badgeColor={getTypeBadgeColor(item.softwarePackageType)}
            badgeSize={TextBadgeSize.small}
            styles={{ root: { width: "fit-content" } }}
          >
            {item.softwarePackageType ?? "--"}
          </TextBadge>
        </Stack>
      )
    },
    {
      key: "softwarePublisherCode",
      name: "Publisher",
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
      onRender: (item: SoftwarePackageDefaultSummaryDto) => (
        <Stack
          wrap
          styles={{
            root: { whiteSpace: "initial" }
          }}
        >
          <Text>{item.softwarePublisherCode}</Text>
        </Stack>
      )
    },
    {
      name: "Software Package",
      key: "name",
      minWidth: 150,
      maxWidth: 450,
      onRender: (item: SoftwarePackageDefaultSummaryDto) => item.name
    },
    {
      name: "Install Type",
      key: "installType",
      minWidth: 150,
      maxWidth: 150,
      onRender: (item: SoftwarePackageDefaultSummaryDto) => (
        <SoftwarePackageDefaultInstallTypeForm softwarePackage={item} />
      )
    },
    {
      name: "Current Version",
      key: "version",
      minWidth: 150,
      maxWidth: 150,
      onRender: (item: SoftwarePackageDefaultSummaryDto) => (
        <SoftwarePackageDefaultCurrentVersion softwarePackage={item} />
      )
    },
    {
      name: "New Version",
      key: "form",
      minWidth: 400,
      onRender: (item: SoftwarePackageDefaultSummaryDto) => (
        <SoftwarePackageDefaultHost softwarePackage={item} />
      )
    }
  ];

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  return (
    <Stack tokens={{ childrenGap: theme.spacing.m }}>
      <SectionTitle>Default Field Config</SectionTitle>
      <ShimmeredDetailsList
        enableShimmer={isLoading}
        errorMessage={error?.message}
        columns={columns}
        items={items ?? []}
        onRenderRow={row => (row ? <DetailsRow {...row} /> : null)}
      />
      <Stack
        horizontal
        tokens={{ childrenGap: theme.spacing.s1 }}
        horizontalAlign="end"
      >
        <TooltipHost content={REGENERATE_TOOLTIP}>
          <DefaultButton
            disabled={!hasDeployPermission}
            title={!hasDeployPermission ? NO_ACTION_PERMISSION : undefined}
            onClick={regenerateDefaultFieldConfig}
          >
            Regenerate Default Configs
          </DefaultButton>
        </TooltipHost>
        <TooltipHost content={PURGE_TOOLTIP}>
          <DangerButton
            disabled={!hasWritePermission}
            title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
            onClick={purgeFieldmanCDN}
          >
            Purge CDN
          </DangerButton>
        </TooltipHost>
      </Stack>
    </Stack>
  );
};

function getSummaryData(
  softwarePackageDefaults: SoftwarePackageDefault[],
  softwarePackages: SoftwarePackage[],
  softwarePublishers: SoftwarePublisher[]
) {
  if (!softwarePackages) return undefined;

  const results = softwarePackages
    .filter(softwarePackage =>
      validSoftwarePackageTypes.includes(softwarePackage.softwarePackageType)
    )
    .map(softwarePackage => {
      const softwarePackageDefault = softwarePackageDefaults?.find(
        x => x.softwarePackageId === softwarePackage?.id
      );

      const softwarePublisher = softwarePublishers?.find(
        x => x.id === softwarePackage?.softwarePublisherId
      );

      return {
        ...softwarePackageDefault,
        softwarePackageId: softwarePackage?.id,
        code: softwarePackage?.code,
        name: softwarePackage?.displayName,
        softwarePackageType: softwarePackage?.softwarePackageType,
        softwarePublisherCode: softwarePublisher?.code
      } as SoftwarePackageDefaultSummaryDto;
    });

  return results;
}

export const FieldConfigScreen = withPermissions(
  FieldConfigScreenBase,
  [
    Permissions.PltFieldManagementRead,
    Permissions.PltFieldManagementWrite,
    Permissions.PltFieldManagementDeploy
  ],
  "or"
);
